/**
 * Auth API - Authentication and user management.
 */

import apiClient, { type RequestConfig } from './client';
import type {
  User,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
} from './types';

const ENDPOINT = '/auth';

// Token storage keys
const USER_KEY = 'eca_user';
const SESSION_EXPIRES_AT_KEY = 'eca_session_expires_at';
const SESSION_HINT_COOKIE_NAME = 'eca_session';
const COOKIE_AUTH_MODE_HEADER = 'X-ECA-Auth-Mode';
export const AUTH_SESSION_UPDATED_EVENT = 'auth:session-updated';

// Initialize auth from storage
let currentUser: User | null = null;

function sessionHintCookieValue() {
  if (typeof document === 'undefined') return null;
  const cookie = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(`${SESSION_HINT_COOKIE_NAME}=`));
  return cookie ? cookie.split('=')[1] : null;
}

function hasSessionHint() {
  return sessionHintCookieValue() === '1';
}

function getStoredSessionExpiresAt(): number | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(SESSION_EXPIRES_AT_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function emitSessionUpdated(expiresAt: number | null) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(AUTH_SESSION_UPDATED_EVENT, {
      detail: { expiresAt },
    })
  );
}

function saveSessionExpiry(expiresIn?: number | null) {
  if (typeof localStorage === 'undefined') return;
  if (typeof expiresIn === 'number' && expiresIn > 0) {
    const expiresAt = Date.now() + expiresIn * 1000;
    localStorage.setItem(SESSION_EXPIRES_AT_KEY, String(expiresAt));
    emitSessionUpdated(expiresAt);
    return;
  }

  emitSessionUpdated(getStoredSessionExpiresAt());
}

function setSessionHint() {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${SESSION_HINT_COOKIE_NAME}=1; Path=/; SameSite=Lax${secure}`;
}

function clearSessionHint() {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${SESSION_HINT_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
}

function loadFromStorage() {
  try {
    if (!hasSessionHint()) {
      clearStorage();
      return;
    }
    const userJson = localStorage.getItem(USER_KEY);
    if (userJson) {
      currentUser = JSON.parse(userJson);
    }
  } catch {
    // Invalid stored data
    clearStorage();
  }
}

function clearStorage() {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(SESSION_EXPIRES_AT_KEY);
  localStorage.removeItem('lastMatterId');
  currentUser = null;
  clearSessionHint();
  emitSessionUpdated(null);
}

function saveUser(user: User, expiresIn?: number | null) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.removeItem('lastMatterId');
  currentUser = user;
  setSessionHint();
  saveSessionExpiry(expiresIn);
}

function cookieAuthConfig(config?: RequestConfig): RequestConfig {
  const headers = new Headers(config?.headers);
  headers.set(COOKIE_AUTH_MODE_HEADER, 'cookie');
  return {
    ...config,
    headers,
  };
}

// Configure API client auth
apiClient.setAuthTokenGetter(() => null);
apiClient.setAuthRefresher(async () => {
  if (!hasSessionHint()) return null;

  try {
    await authApi.refreshToken(undefined, { skipAuth: true });
    return '';
  } catch {
    clearStorage();
    return null;
  }
});

// Auth interceptors simplified for production
apiClient.addErrorInterceptor((error) => {
  if (error.status === 401) {
    clearStorage();
    // Dispatch custom event for AuthContext to handle
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
  return error;
});

// Load initial state
loadFromStorage();

export const authApi = {
  /**
   * Login with email and password.
   */
  async login(request: LoginRequest, config?: RequestConfig): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>(
      `${ENDPOINT}/login`,
      request,
      { ...cookieAuthConfig(config), skipAuth: true }
    );
    saveUser(data.user, data.expires_in);
    return data;
  },

  /**
   * Logout current user.
   */
  async logout(config?: RequestConfig): Promise<void> {
    try {
      await apiClient.post(`${ENDPOINT}/logout`, null, config);
    } finally {
      clearStorage();
    }
  },

  /**
   * Refresh access token.
   */
  async refreshToken(
    request?: RefreshTokenRequest,
    config?: RequestConfig
  ): Promise<RefreshTokenResponse> {
    const requestConfig = request?.refresh_token
      ? { ...config, skipAuth: true }
      : { ...cookieAuthConfig(config), skipAuth: true };
    const { data } = await apiClient.post<RefreshTokenResponse>(
      `${ENDPOINT}/refresh`,
      request,
      requestConfig
    );
    saveSessionExpiry(data.expires_in);
    return data;
  },

  /**
   * Get current user.
   */
  async getCurrentUser(config?: RequestConfig): Promise<User> {
    const { data } = await apiClient.get<User>(`${ENDPOINT}/me`, config);
    saveUser(data);
    return data;
  },

  /**
   * Update current user profile.
   */
  async updateProfile(
    updates: { name?: string; email?: string },
    config?: RequestConfig
  ): Promise<User> {
    const { data } = await apiClient.patch<User>(`${ENDPOINT}/me`, updates, config);
    saveUser(data);
    return data;
  },

  /**
   * Change password.
   */
  async changePassword(
    request: { current_password: string; new_password: string },
    config?: RequestConfig
  ): Promise<void> {
    await apiClient.post(`${ENDPOINT}/change-password`, request, config);
  },

  /**
   * Request password reset.
   */
  async requestPasswordReset(email: string, config?: RequestConfig): Promise<void> {
    await apiClient.post(
      `${ENDPOINT}/forgot-password`,
      { email },
      { ...config, skipAuth: true }
    );
  },

  /**
   * Reset password with token.
   */
  async resetPassword(
    request: { token: string; new_password: string },
    config?: RequestConfig
  ): Promise<void> {
    await apiClient.post(`${ENDPOINT}/reset-password`, request, { ...config, skipAuth: true });
  },

  /**
   * Register a new user (if registration is enabled).
   */
  async register(
    request: {
      email: string;
      password: string;
      name: string;
    },
    config?: RequestConfig
  ): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>(
      `${ENDPOINT}/register`,
      request,
      { ...config, skipAuth: true }
    );
    return data;
  },

  /**
   * Self-service signup for new law firms.
   * Creates tenant (trial) + user (admin) + returns JWT for auto-login.
   */
  async signup(
    request: {
      email: string;
      password: string;
      firm_name: string;
    },
    config?: RequestConfig
  ): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>(
      `${ENDPOINT}/signup`,
      request,
      { ...cookieAuthConfig(config), skipAuth: true }
    );
    saveUser(data.user, data.expires_in);
    return data;
  },

  /**
   * Verify email with token.
   */
  async verifyEmail(token: string, config?: RequestConfig): Promise<void> {
    await apiClient.post(
      `${ENDPOINT}/verify-email`,
      { token },
      { ...config, skipAuth: true }
    );
  },

  /**
   * Check if user is authenticated.
   */
  isAuthenticated(): boolean {
    return hasSessionHint();
  },

  /**
   * Get cached current user (synchronous).
   */
  getCachedUser(): User | null {
    return currentUser;
  },

  /**
   * Get access token (for external use).
   */
  getAccessToken(): string | null {
    return null;
  },

  hasSessionHint(): boolean {
    return hasSessionHint();
  },

  getSessionExpiresAt(): number | null {
    return getStoredSessionExpiresAt();
  },

  getSessionRemainingMs(): number | null {
    const expiresAt = getStoredSessionExpiresAt();
    return expiresAt ? Math.max(expiresAt - Date.now(), 0) : null;
  },

  /**
   * Clear auth state (for testing or manual logout).
   */
  clearAuth() {
    clearStorage();
  },

  /**
   * Check if access token is expired.
   */
  isTokenExpired(): boolean {
    return !hasSessionHint();
  },
};

export default authApi;
